type CustomType = {
  id: string;
};

export type CustomLineItem = {
  key: string;
  totalPrice: Money;
  custom: CustomOnLineItem;
  quantity: number;
  discountedPricePerQuantity: DiscountedPricePerQuantity[];
  taxedPrice: {
    taxPortions: TaxPortion[];
    totalGross: Money;
    totalNet: Money;
    totalTax: Money;
  };
};

export type SprdConfigurationDetails = {
  configurations?: { printAreaId?: string; printTypeId?: string }[];
  productTypeId?: string;
};

type CustomLineItemFields = {
  sprdProductLookupId: string;
  sprdShopId: number;
  sprdProductId: number;
  sprdDiscountAmount: Money;
  sprdEditLink: string;
  sprdSellableId: string | undefined;
  sprdCompositionId: string | undefined;
  sprdConfigurationDetails: string | undefined;
  /**
   * @deprecated Use sprdTrackingJson instead.
   */
  sprdCompositionsJson: string | undefined;
  sprdTrackingJson: string | undefined;
};

type CustomOnLineItem = {
  type: CustomType;
  fields: CustomLineItemFields;
};

type CustomOnCart = {
  type: CustomType;
  fields: {
    subscribeToNewsletter?: boolean;
    sprdVoucherList?: string;
    sprdVoucherJson?: string;
    sprdGiftWrappingMessage?: string;
    sprdCouponConsumptionIds?: string[];
  };
};

type Attribute = {
  name: string;
  value: number;
};

type Variant = {
  id: number;
  sku: string;
  key: string;
  attributes: Attribute[];
};

export type Money = {
  centAmount: number;
  currencyCode: string;
  fractionDigits: number;
};

type SimpleMoney = {
  centAmount: number;
  currencyCode: string;
};

type Price = {
  id: string;
  key: string;
  value: Money;
};

type State = {
  id: string;
  key: string;
};

type UserAuditInfo = {
  clientId: string;
  externalUserId: string;
  anonymousId: string;
};

export interface Address {
  firstName: string;
  lastName: string;
  streetName: string;
  additionalAddressInfo?: string;
  postalCode: string;
  city: string;
  state?: string;
  company?: string;
  country: string;
  salutation?: string;
  phone?: string;
  custom?: {
    fields: {
      vatId: string;
    };
  };
}

export type ShippingInfo = {
  shippingMethodName: string;
  price: Money;
  shippingMethod: {
    id: string;
    key: string;
    obj: null;
    typeId: string;
  };
  taxedPrice: {
    totalNet: Money;
  };
  discountedPrice?: DiscountedPrice;
};

type CentPrecisionMoney = Money & { type: 'centPrecision' };

type CartScoreShippingRate = {
  score: number;
  price: CentPrecisionMoney;
};

export type ShippingMethod = {
  id: string;
  name: string;
  key: string;
  price: CentPrecisionMoney;
  // Pickup/access-point methods can be returned without an ETA.
  deliveryETA?: {
    earliestDateUTC: string;
    latestDateUTC: string;
    minDays: number;
    maxDays: number;
  };
  supportsPackStation: boolean;
  supportsUpsPickup: boolean;
  isExpress: boolean;
  phoneNumberRequired: boolean;
  shippingTypeId: number;
  cartScoreShippingRates: CartScoreShippingRate[];
};

export type ShippingMethodsResponse = {
  shippingMethods: ShippingMethod[];
  shippingOptions: { giftWrapping?: { price: CentPrecisionMoney } };
};

type IncludedDiscount = {
  discount: {
    id: string;
    typeId: string;
  };
  discountedAmount: Money;
};

type DiscountedPrice = {
  value: Money;
  includedDiscounts: IncludedDiscount[];
};

type DiscountedPricePerQuantity = {
  quantity: number;
  discountedPrice: DiscountedPrice;
};

export interface LineItem {
  id: string;
  productId: string;
  productKey: string;
  name: Record<string, string>;
  productSlug: Record<string, string>;
  productType: { id: string };
  variant: Variant;
  price: Price;
  quantity: number;
  totalPrice: Money;
  discountedPricePerQuantity: DiscountedPricePerQuantity[];
  state: {
    quantity: number;
    state: State;
  }[];
  distributionChannel: { id: string; obj: { key: string } };
  custom: CustomOnLineItem;
  addedAt: string;
  lastModifiedAt: string;
  taxedPrice: {
    taxPortions: TaxPortion[];
    totalGross: Money;
    totalNet: Money;
    totalTax: Money;
  };
}

export type TaxPortion = {
  name: string;
  rate: number;
  amount: Money;
};

export enum CartStates {
  ACTIVE = 'Active',
  MERGED = 'Merged',
  ORDERED = 'Ordered',
  FROZEN = 'Frozen',
}

export interface CommerceToolsCart {
  id: string;
  version: number;
  country: string;
  lineItems: LineItem[];
  customLineItems: CustomLineItem[];
  totalLineItemQuantity: number;
  totalPrice: Money;
  taxedPrice: {
    taxPortions: TaxPortion[];
    totalNet: Money;
    totalTax: Money;
  };
  taxedShippingPrice: {
    totalNet: Money;
    totalGross: Money;
    totalTax: Money;
  };
  //discountOnTotalPrice: null;
  cartState: CartStates;
  customerEmail: string;
  billingAddress: Address;
  shippingAddress: Address;
  /** Absent until a shipping method is set — notably on a cart with no line items. */
  shippingInfo?: ShippingInfo;
  custom: CustomOnCart;
  createdAt: string;
  lastModifiedAt: string;
  lastModifiedBy: UserAuditInfo;
  createdBy: UserAuditInfo;
}

type VoucherDirectDiscout = {
  id: string;
  value: {
    type: string;
    money: SimpleMoney[];
  };
};

type VoucherMoneyTypeCoupon = {
  code: string;
  type: string;
  appliedAmount: Money;
};

export interface SprdVoucherData {
  directDiscounts: VoucherDirectDiscout[];
  redeemableMoneyTypeCoupons: VoucherMoneyTypeCoupon[];
}

export type TrackedTaxPortion = {
  name: string;
  rate: number;
  amount?: number;
};

export type PriceBreakdown = {
  lineItemGroupInclVat: number;
  lineItemGroupExclVat: number;
  lineItemGroupTaxRates: TrackedTaxPortion[];
  commissionInclVat: number;
  commissionExclVat: number;
  commissionTaxRates: TrackedTaxPortion[];
  decorationInclVat: number;
  decorationExclVat: number;
  decorationTaxRates: TrackedTaxPortion[];
  totalPrice: number;
  totalDiscount: number;
  totalDirectDiscount: number;
  totalVolumeDiscount: number;
};

export type LineItemGroup = {
  name: string;
  groupId: string;
  sellableId: string | undefined;
  productId: number;
  productLookupId: string;
  productTypeId: number;
  appearanceId: number;
  shopId: number;
  groupQuantity: number;
  groupTotalCentAmount: number;
  currencyCode: string;
  outOfStockItems: { sizeId: number }[];
  lineItems: LineItem[];
  priceBreakdown: PriceBreakdown;
  configurationDetails: SprdConfigurationDetails | undefined;
};

export type CartAndLineItemGroups = {
  cart: CommerceToolsCart;
  lineItemGroups: LineItemGroup[];
};

export interface CommercetoolsOrder {
  id: string;
  version: number;
  totalPrice: Money;
  taxedPrice: {
    taxPortions: TaxPortion[];
    totalTax: Money;
    totalNet: Money;
  };
  taxedShippingPrice: {
    totalNet: Money;
  };
}

export type ErrorResult = {
  code: string | number;
  data: {
    couponCodes: string[];
  };
  message: string;
  causes: {
    code: string;
    message: string;
  }[];
};
